/*
 * Pure helpers for the exam results overview and the participant's own exam
 * cards: which attempt counts, what status an invited user is in, and the
 * deadline that applies to them. Nothing here touches stores or the DOM.
 */

export const RESULT_STATUS = Object.freeze({
    notStarted: 'notStarted',
    overdue: 'overdue',
    awaiting: 'awaiting',
    failed: 'failed',
    passed: 'passed',
    signed: 'signed',
});

const STATUS_RANK = Object.freeze({
    signed: 6, passed: 5, awaiting: 4, failed: 3, overdue: 2, notStarted: 1,
});

export function toDate(value) {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

export function invitedId(entry) {
    if (entry && typeof entry === 'object') return Number(entry.id) || 0;
    return Number(entry) || 0;
}

export function findInvitedEntry(exam, userId) {
    const id = Number(userId) || 0;
    const invited = exam && Array.isArray(exam.invitedUsers) ? exam.invitedUsers : [];
    return invited.find(entry => invitedId(entry) === id) || null;
}

/** The invitation's own deadline, else the exam-wide one, else null. */
export function effectiveDeadline(exam, userId) {
    const entry = findInvitedEntry(exam, userId);
    return toDate(entry && entry.deadline) || toDate(exam && exam.deadline);
}

export function isDeadlinePassed(exam, userId, now = new Date()) {
    const deadline = effectiveDeadline(exam, userId);
    return !!deadline && deadline.getTime() < now.getTime();
}

function attemptTime(attempt) {
    const date = toDate(attempt && (attempt.submittedDate || attempt.createdDate));
    return date ? date.getTime() : 0;
}

/**
 * The attempt that represents a user: a signed diploma beats a passed attempt,
 * which beats one awaiting review, which beats a failed one. Newest wins ties.
 */
export function bestAttempt(attempts = []) {
    const list = Array.isArray(attempts) ? attempts.filter(Boolean) : [];
    if (list.length === 0) return null;
    const rank = attempt => {
        if (attempt.diploma > 0) return 4;
        if (attempt.passed) return 3;
        if (!attempt.approvalStatus || attempt.approvalStatus === 'pending') return 2;
        return 1;
    };
    return list.reduce((best, attempt) => {
        if (!best) return attempt;
        const diff = rank(attempt) - rank(best);
        if (diff > 0) return attempt;
        if (diff < 0) return best;
        return attemptTime(attempt) > attemptTime(best) ? attempt : best;
    }, null);
}

export function latestAttempt(attempts = []) {
    const list = Array.isArray(attempts) ? attempts.filter(Boolean) : [];
    return list.reduce((latest, attempt) => (
        !latest || attemptTime(attempt) > attemptTime(latest) ? attempt : latest
    ), null);
}

/**
 * Status for one invited user given their attempts on the exam.
 */
export function deriveStatus({ exam, userId, attempts = [], now = new Date() } = {}) {
    const best = bestAttempt(attempts);
    if (best) {
        if (best.diploma > 0) return RESULT_STATUS.signed;
        if (best.passed) return RESULT_STATUS.passed;
        if (!best.approvalStatus || best.approvalStatus === 'pending') return RESULT_STATUS.awaiting;
        return RESULT_STATUS.failed;
    }
    return isDeadlinePassed(exam, userId, now) ? RESULT_STATUS.overdue : RESULT_STATUS.notStarted;
}

/**
 * One row per invited user for the results table. `attempts` is every attempt
 * on the exam (any user); `courseId` filters to one course's invitations.
 */
export function buildResultRows({
    exam, attempts = [], now = new Date(), courseId = 0,
} = {}) {
    const invited = exam && Array.isArray(exam.invitedUsers) ? exam.invitedUsers : [];
    const byUser = {};
    (Array.isArray(attempts) ? attempts : []).forEach(attempt => {
        const id = Number(attempt && attempt.user) || 0;
        if (!id) return;
        if (!byUser[id]) byUser[id] = [];
        byUser[id].push(attempt);
    });
    return invited
        .map(entry => (typeof entry === 'object' ? entry : { id: entry }))
        .filter(entry => invitedId(entry) > 0)
        .filter(entry => !courseId || Number(entry.course) === Number(courseId))
        .map(entry => {
            const id = invitedId(entry);
            const userAttempts = byUser[id] || [];
            const best = bestAttempt(userAttempts);
            const latest = latestAttempt(userAttempts);
            return {
                id,
                name: `${entry.firstname || ''} ${entry.lastname || ''}`.trim() || `#${id}`,
                email: entry.email || '',
                course: Number(entry.course) || 0,
                invitedDate: toDate(entry.invitedDate),
                deadline: effectiveDeadline(exam, id),
                status: deriveStatus({ exam, userId: id, attempts: userAttempts, now }),
                attemptCount: userAttempts.length,
                bestAttempt: best,
                lastAttemptDate: latest ? toDate(latest.submittedDate || latest.createdDate) : null,
            };
        })
        .sort((a, b) => (STATUS_RANK[b.status] - STATUS_RANK[a.status]) || a.name.localeCompare(b.name, 'nb'));
}

export function formatDateTime(value) {
    const date = toDate(value);
    if (!date) return '';
    return date.toLocaleString('nb-NO', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

/** Local datetime-local input value (YYYY-MM-DDTHH:mm) for a date, or ''. */
export function toInputDateTime(value) {
    const date = toDate(value);
    if (!date) return '';
    const pad = n => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Deep links under /exam/:rest*: "results/12/7" → the results overview,
 * "take/12" → straight into the exam. Anything else → null (the list).
 */
export function parseExamRoute(rest) {
    const parts = String(rest || '').split('/').filter(Boolean);
    const examId = parseInt(parts[1], 10);
    if (!(examId > 0)) return null;
    if (parts[0] === 'results') {
        return { view: 'results', examId, courseId: parseInt(parts[2], 10) || 0 };
    }
    if (parts[0] === 'take') {
        return { view: 'taking', examId, courseId: 0 };
    }
    return null;
}

export const COURSE_EXAM_STATUS = Object.freeze({
    open: 'open',
    overdue: 'overdue',
    passed: 'passed',
});

/**
 * What the course page shows an invited participant: null when the exam was
 * never sent to this user, otherwise { status, deadline } where status is
 * 'passed' (a passed attempt exists), 'overdue' or 'open'.
 */
export function courseExamAccess({
    exam, userId, attempts = [], now = new Date(),
} = {}) {
    const id = Number(userId) || 0;
    if (!exam || !(exam.id > 0) || !id || !findInvitedEntry(exam, id)) return null;
    const deadline = effectiveDeadline(exam, id);
    const passed = (Array.isArray(attempts) ? attempts : [])
        .some(a => a && Number(a.exam) === Number(exam.id) && Number(a.user) === id && a.passed);
    if (passed) return { status: COURSE_EXAM_STATUS.passed, deadline };
    if (isDeadlinePassed(exam, id, now)) return { status: COURSE_EXAM_STATUS.overdue, deadline };
    return { status: COURSE_EXAM_STATUS.open, deadline };
}

export const DEFAULT_DEADLINE_MS = 2 * 60 * 60 * 1000;

/** Default deadline suggestion: 2 hours from now, whole minutes (the input shows minutes). */
export function defaultDeadline(now = new Date()) {
    const date = new Date(now.getTime() + DEFAULT_DEADLINE_MS);
    date.setSeconds(0, 0);
    return date;
}
